import type { SlotsChangedEvent } from './stay-sync';

// The room board as the API returns it (API_SPECIFICATION.md §9) and the pure rule that applies a real-time slot change
// to it. Shared by the passenger app and the backoffice so both screens behave identically.

export type StayStatus = 'DRAFT' | 'OPEN' | 'CONFIRMED' | 'REOPENED' | 'CLOSED';
/** HELD: the viewer's own request for this bed is waiting for an administrator ("en espera"); others see OCCUPIED. */
export type SlotState = 'FREE' | 'OCCUPIED' | 'MINE' | 'HELD';

export interface BoardSlot {
  readonly id: string;
  readonly position: number;
  readonly state: SlotState;
  /** Only staff and the group's guardians receive occupants. */
  readonly occupant?: { readonly memberId: string; readonly firstNames: string; readonly lastNames: string };
}

export interface BoardRoom {
  readonly id: string;
  readonly label: string;
  readonly sexRestriction: 'FEMALE' | 'MALE' | 'ANY';
  /** Who the room is for: the students (default) or the companions, the parents who travel (2026-10-28). */
  readonly audience?: 'PASSENGER' | 'COMPANION';
  readonly capacity: number;
  /** Whether the viewer may choose a slot here (passengers only). */
  readonly bookable: boolean;
  /** Choosing a bed here sends a request an administrator approves (undefined sex in a women's or men's room). */
  readonly needsApproval: boolean;
  readonly physicalLabel?: string | null;
  readonly slots: readonly BoardSlot[];
}

export interface Board {
  readonly stayId: string;
  readonly version: number;
  readonly status: StayStatus;
  /** OPEN/REOPENED inside the window: passengers choose and change freely. */
  readonly selectionOpen: boolean;
  /** Inside the window and not closed: after confirmation, a slot for whoever has none and agreed exchanges. */
  readonly windowOpen: boolean;
  /** Whose rooms the viewer sees: a student or a companion; null for staff, guardians, hotel and guide (they see both). */
  readonly viewerRoomAudience?: 'PASSENGER' | 'COMPANION' | null;
  /** The hotel's terms and conditions, or null when the hotel loaded none (then no checkbox). */
  readonly hotelTerms: HotelTerms | null;
  readonly rooms: readonly BoardRoom[];
}

export interface HotelTerms {
  readonly url: string | null;
  readonly text: string | null;
  /** The terms as a file (PDF, image, Word); open it at `/api/v1/stays/{id}/hotel-terms/file`. */
  readonly file: { readonly name: string; readonly type: string; readonly size: number } | null;
  readonly version: number;
  /** The passenger already accepted THIS version. */
  readonly accepted: boolean;
}

export type ApplyResult = { readonly kind: 'applied'; readonly board: Board } | { readonly kind: 'resync' };

/**
 * Applies an IN-ORDER `slots.changed` event (the caller already checked `decideEvent`). Asks for a re-read when the
 * event cannot be applied faithfully: an unknown slot, or a new occupant whose name this screen does not have.
 */
export function applySlotChanges(board: Board, event: SlotsChangedEvent): ApplyResult {
  const changes = new Map(event.slots.map((change) => [change.slotId, change]));
  const names = new Map<string, NonNullable<BoardSlot['occupant']>>();
  for (const room of board.rooms)
    for (const slot of room.slots) if (slot.occupant) names.set(slot.occupant.memberId, slot.occupant);

  const known = new Set(board.rooms.flatMap((room) => room.slots.map((slot) => slot.id)));
  // The viewer's own room (a passenger sees the names of their roommates): any change there, or a change of the
  // viewer's own slot, is re-read — the event carries no names for passengers.
  const myRoom = board.rooms.find((room) => room.slots.some((slot) => slot.state === 'MINE'));
  const touchesMyRoom = event.slots.some(
    (change) => change.state === 'MINE' || myRoom?.slots.some((slot) => slot.id === change.slotId) === true,
  );
  const unfaithful = event.slots.some(
    (change) =>
      !known.has(change.slotId) || (change.occupantMemberId !== undefined && !names.has(change.occupantMemberId)),
  );
  if (unfaithful || touchesMyRoom) return { kind: 'resync' };

  const rooms = board.rooms.map((room) => ({
    ...room,
    slots: room.slots.map((slot): BoardSlot => {
      const change = changes.get(slot.id);
      if (change === undefined) return slot;
      const { occupant: _previous, ...rest } = slot;
      const occupant = change.occupantMemberId === undefined ? undefined : names.get(change.occupantMemberId);
      return occupant === undefined ? { ...rest, state: change.state } : { ...rest, state: change.state, occupant };
    }),
  }));
  return { kind: 'applied', board: { ...board, version: event.stayVersion, rooms } };
}

/** The viewer's own slot, if any (state MINE). */
export function mySlotId(board: Board): string | undefined {
  for (const room of board.rooms) for (const slot of room.slots) if (slot.state === 'MINE') return slot.id;
  return undefined;
}

/** Free slots / total of a room, for the room card's summary line. */
export function freeCount(room: BoardRoom): number {
  return room.slots.filter((slot) => slot.state === 'FREE').length;
}

/** Progress of a stay's distribution (`GET /stays/{id}/distribution`): for staff, guardians and coordinators. */
export interface Distribution {
  readonly stayId: string;
  readonly version: number;
  readonly status: StayStatus;
  readonly selectionOpen: boolean;
  readonly passengers: number;
  readonly assigned: number;
  readonly unassigned: number;
  readonly slots: number;
  readonly freeSlots: number;
  readonly percentAssigned: number;
  readonly missing: readonly {
    readonly memberId: string;
    readonly firstNames: string;
    readonly lastNames: string;
    readonly sex: string;
  }[];
  /** Share of passengers with a slot from which the signing guardian may confirm (80). */
  readonly thresholdPercent: number;
  /** The threshold is reached and the distribution is still open. */
  readonly confirmable: boolean;
  /** The viewer is the group's designated signing guardian. */
  readonly viewerIsSigner: boolean;
  /** The confirmation in force, when the distribution is confirmed. */
  readonly confirmation: { readonly confirmedAt: string; readonly assigned: number; readonly eligible: number } | null;
}

/** A person's sex in words, the same in every screen of both apps. */
export const PERSON_SEX_TEXT: Readonly<Record<string, string>> = {
  FEMALE: 'Mujer',
  MALE: 'Varón',
  UNDEFINED: 'Sin definir',
};

/** The missing passengers as display chips ("Ana Rojas · Mujer"). */
export function missingChips(distribution: Distribution): { id: string; text: string }[] {
  return distribution.missing.map((member) => ({
    id: member.memberId,
    text: `${member.firstNames} ${member.lastNames} · ${PERSON_SEX_TEXT[member.sex] ?? member.sex}`,
  }));
}
